import { ExperienceManager, PageResult } from '../../models';
import { ApiError, CapabilityNotEnabledError, NotFoundError } from '../../components/errors';
import { CCApiService } from './api-service';
import { UIConfig } from './config-service';

/**
 * API service for Experience Manager operations
 * Handles CRUD operations and cross-linking functionality for Experience Manager instances
 */
export class ExperienceManagerApiService {
  static ApiService: CCApiService;
  static apiBasePath: string;

  /**
   * Initialize the Experience Manager API service
   * @param config UI configuration containing API settings
   */
  static init(config: UIConfig) {
    this.ApiService = new CCApiService(config.authClientId, config.authIssuerUri);
    this.apiBasePath = config.apiBasePath;
  }

  /**
   * Check if we're in development mode (localhost)
   * @returns true if running on localhost, false otherwise
   */
  static isDevelopmentMode(): boolean {
    return window.location.hostname === 'localhost';
  }

  /**
   * Transform API response to match our Experience Manager model structure
   * @param apiResponse Raw API response object
   * @returns Transformed Experience Manager object
   */
  static transformApiResponseToModel(apiResponse: any): ExperienceManager {
    return {
      // Core fields
      id: apiResponse.id,
      name: apiResponse.name,
      type: apiResponse.type,
      state: apiResponse.state,
      region: apiResponse.region,
      tags: apiResponse.tags || [],

      // Customer information - transform customerId/customerName to customer object
      customer: {
        id: apiResponse.customerId,
        name: apiResponse.customerName,
      },

      // Additional API fields
      customerId: apiResponse.customerId,
      customerName: apiResponse.customerName,
      fusionInstance: apiResponse.fusionInstance,
      fusionAppId: apiResponse.fusionAppId,
      signalStore: apiResponse.signalStore,
      live: apiResponse.live,
      migrate: apiResponse.migrate,
      migrationStatus: apiResponse.migrationStatus,
      cell: apiResponse.cell,
      activeTaskCount: apiResponse.activeTaskCount,

      // Timestamps - map API field names to both model conventions
      createdAt: apiResponse.createdAt,
      updatedAt: apiResponse.updatedAt,
      creationDate: apiResponse.createdAt, // For Auditable interface compatibility
      lastModified: apiResponse.updatedAt, // For Auditable interface compatibility

      // Site environment mapping if provided
      siteEnvironment: apiResponse.siteEnvironment
        ? {
            id: apiResponse.siteEnvironment.id || apiResponse.siteEnvironment,
            name: apiResponse.siteEnvironment.name || apiResponse.siteEnvironment,
            // Include site information if available, but filter out UUIDs for site names
            ...(apiResponse.siteEnvironment.site && {
              site: {
                id: apiResponse.siteEnvironment.site.id,
                name:
                  apiResponse.siteEnvironment.site.name ||
                  // Don't use site ID as name if it looks like a UUID
                  (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                    apiResponse.siteEnvironment.site.id
                  )
                    ? undefined
                    : apiResponse.siteEnvironment.site.id),
              },
            }),
            // Include siteName if available and not a UUID
            ...(apiResponse.siteEnvironment.siteName &&
              !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                apiResponse.siteEnvironment.siteName
              ) && {
                siteName: apiResponse.siteEnvironment.siteName,
              }),
          }
        : undefined,

      // Other optional fields
      version: apiResponse.version,
      endpoint: apiResponse.endpoint,
      isPublic: apiResponse.isPublic,
      configuration: apiResponse.configuration,
    };
  }

  /**
   * Get a paginated list of Experience Manager instances for a customer
   * @param params Parameters including customer ID and pagination
   * @param params.customer Customer ID
   * @param params.size Number of items per page (default: 20)
   * @param params.page Page number (default: 0)
   * @param params.sort Sort specification string (e.g., 'name,asc') - will be converted to array for API
   * @returns Promise resolving to paginated Experience Manager results
   */
  static async getExperienceManagerInstances(params: {
    customer: string;
    size?: number;
    page?: number;
    sort?: string;
  }): Promise<PageResult<ExperienceManager>> {
    const url = `${this.apiBasePath}/customers/${params.customer}/experience-manager-instances`;

    // Build query parameters for Spring Boot Pageable
    const queryParams = new URLSearchParams();
    queryParams.set('page', (params.page || 0).toString());
    queryParams.set('size', (params.size || 20).toString());

    // Add sort parameter - can be multiple values
    const sortParam = params.sort || 'name,asc';
    queryParams.set('sort', sortParam);

    const finalUrl = `${url}?${queryParams.toString()}`;
    const response = (await this.ApiService.performFetch(finalUrl).catch(
      this._mapApiErrorCodeToMessage
    )) as PageResult<any>;

    // Transform the API response content to match our model structure
    if (response.content && Array.isArray(response.content)) {
      response.content = response.content.map((item: any) => this.transformApiResponseToModel(item));
    }

    return response as PageResult<ExperienceManager>;
  }
  /**
   * Get a specific Experience Manager instance by ID
   * @param customerId Customer ID that owns the instance
   * @param instanceId Experience Manager instance ID
   * @returns Promise resolving to the Experience Manager instance
   */
  static async getExperienceManagerInstance(customerId: string, instanceId: string): Promise<ExperienceManager> {
    // Use correct API path from OpenAPI spec
    const url = `${this.apiBasePath}/customers/${customerId}/experience-manager-instances/${instanceId}`;
    const response = (await this.ApiService.performFetch(url).catch(this._mapApiErrorCodeToMessage)) as any;

    // Transform the API response to match our model structure
    return this.transformApiResponseToModel(response);
  }

  /**
   * Create a new Experience Manager instance
   * @param customerId Customer ID to create the instance for
   * @param instance Experience Manager instance data
   * @returns Promise resolving to the created instance
   */
  static async createExperienceManagerInstance(
    customerId: string,
    instance: ExperienceManager
  ): Promise<ExperienceManager> {
    // Use correct API path from OpenAPI spec
    const url = `${this.apiBasePath}/customers/${customerId}/experience-manager-instances`;
    const fetchOptions = {
      method: 'POST',
      body: JSON.stringify(instance),
    };
    return this.ApiService.performFetch(url, fetchOptions).catch(
      this._mapApiErrorCodeToMessage
    ) as Promise<ExperienceManager>;
  }

  /**
   * Update an existing Experience Manager instance
   * @param customerId Customer ID that owns the instance
   * @param instance Experience Manager instance data with updates
   * @returns Promise resolving to the updated instance
   */
  static async updateExperienceManagerInstance(
    customerId: string,
    instance: ExperienceManager
  ): Promise<ExperienceManager> {
    // Use correct API path from OpenAPI spec
    const url = `${this.apiBasePath}/customers/${customerId}/experience-manager-instances/${instance.id}`;
    const fetchOptions = {
      method: 'PUT',
      body: JSON.stringify(instance),
    };
    return this.ApiService.performFetch(url, fetchOptions).catch(
      this._mapApiErrorCodeToMessage
    ) as Promise<ExperienceManager>;
  }

  /**
   * Delete an Experience Manager instance
   * @param customerId Customer ID that owns the instance
   * @param instanceId Experience Manager instance ID to delete
   * @returns Promise resolving when deletion is complete
   */
  static async deleteExperienceManagerInstance(customerId: string, instanceId: string): Promise<void> {
    // Use correct API path from OpenAPI spec
    const url = `${this.apiBasePath}/customers/${customerId}/experience-manager-instances/${instanceId}`;
    const fetchOptions = {
      method: 'DELETE',
    };
    return this.ApiService.performFetch(url, fetchOptions).catch(this._mapApiErrorCodeToMessage) as Promise<void>;
  }

  /**
   * Get the Site Environment associated with an Experience Manager instance
   * @param customerId Customer ID that owns the instance
   * @param instanceId Experience Manager instance ID
   * @returns Promise resolving to the associated Site Environment
   */
  static async getExperienceManagerSiteEnvironment(customerId: string, instanceId: string): Promise<any> {
    // Use correct API path from OpenAPI spec
    const url = `${this.apiBasePath}/customers/${customerId}/experience-manager-instances/${instanceId}/site-environment`;
    return this.ApiService.performFetch(url);
  }

  /**
   * Get the Integrated System associated with an Experience Manager instance
   * @param customerId Customer ID that owns the instance
   * @param instanceId Experience Manager instance ID
   * @returns Promise resolving to the associated Integrated System
   */
  static async getRelatedIntegratedSystem(customerId: string, instanceId: string): Promise<any> {
    // Use correct API path from OpenAPI spec
    const url = `${this.apiBasePath}/customers/${customerId}/experience-manager-instances/${instanceId}/integrated-system`;
    return this.ApiService.performFetch(url);
  }

  /**
   * Get Experience Manager instances associated with a Site Environment
   * @param customerId Customer ID
   * @param siteEnvironmentId Site Environment ID
   * @returns Promise resolving to Experience Manager instances
   */
  static async getExperienceManagersBySiteEnvironment(
    customerId: string,
    siteEnvironmentId: string
  ): Promise<ExperienceManager[]> {
    // Use correct API path from OpenAPI spec
    const url = `${this.apiBasePath}/customers/${customerId}/site-environments/${siteEnvironmentId}/experience-manager-instances`;
    const response = (await this.ApiService.performFetch(url)) as any;
    return (response?.content as ExperienceManager[]) ?? (response as ExperienceManager[]);
  }

  /**
   * Get Experience Manager instances associated with an Integrated System
   * @param customerId Customer ID
   * @param integratedSystemId Integrated System ID
   * @returns Promise resolving to Experience Manager instances
   */
  static async getExperienceManagersByIntegratedSystem(
    customerId: string,
    integratedSystemId: string
  ): Promise<ExperienceManager[]> {
    // Use correct API path from OpenAPI spec
    const url = `${this.apiBasePath}/customers/${customerId}/integrated-systems/${integratedSystemId}/experience-manager-instances`;
    const response = (await this.ApiService.performFetch(url)) as any;
    return (response?.content as ExperienceManager[]) ?? (response as ExperienceManager[]);
  }

  /**
   * Get Experience Manager instances associated with a specific cell
   * @param cellName Cell name to filter by
   * @param params Optional parameters for pagination and sorting
   * @param params.size Number of items per page (default: 20)
   * @param params.page Page number (default: 0)
   * @param params.sort Sort specification string (e.g., 'name,asc')
   * @returns Promise resolving to paginated Experience Manager instances
   */
  static async getExperienceManagersByCell(
    cellName: string,
    params?: {
      size?: number;
      page?: number;
      sort?: string;
    }
  ): Promise<PageResult<ExperienceManager>> {
    const url = `${this.apiBasePath}/experience-manager-instances`;

    // Build query parameters for cell filtering
    const queryParams = new URLSearchParams();
    queryParams.set('cell', cellName);
    queryParams.set('page', (params?.page || 0).toString());
    queryParams.set('size', (params?.size || 20).toString());

    // Add sort parameter
    const sortParam = params?.sort || 'name,asc';
    queryParams.set('sort', sortParam);

    const finalUrl = `${url}?${queryParams.toString()}`;
    const response = (await this.ApiService.performFetch(finalUrl)) as PageResult<any>;

    // Transform the API response content to match our model structure
    if (response.content && Array.isArray(response.content)) {
      response.content = response.content.map((item: any) => this.transformApiResponseToModel(item));
    }

    return response as PageResult<ExperienceManager>;
  }

  /**
   * Get count of Experience Manager instances for a customer
   * @param customerId Customer ID
   * @returns Promise resolving to the count
   */
  static async getCount(customerId: string): Promise<number> {
    // Note: This endpoint may not exist in the OpenAPI spec, using the list endpoint to count
    const response = await this.getExperienceManagerInstances({ customer: customerId, size: 1, page: 0 });
    return response.totalElements || 0;
  }

  /**
   * Get count of Experience Manager instances for a specific cell
   * @param cellName Cell name to filter by
   * @returns Promise resolving to the count
   */
  static async getCountByCell(cellName: string): Promise<number> {
    const response = await this.getExperienceManagersByCell(cellName, { size: 1, page: 0 });
    return response.totalElements || 0;
  }

  /**
   * Map API error codes to user-friendly messages
   * @param error API error object
   * @throws Modified error with user-friendly message
   */
  static _mapApiErrorCodeToMessage(error: any): never {
    // Check for capability-related 412 errors FIRST
    if (error.statusCode === 412 && error.message?.toLowerCase().includes('capability')) {
      throw new CapabilityNotEnabledError('Commerce Studio', error.message);
    }

    // Handle UUID validation errors as NotFoundError
    if (error.message?.includes('Invalid UUID string')) {
      throw new NotFoundError();
    }

    if (error instanceof ApiError) {
      switch (error.message) {
        case 'E0001001':
          error.message = 'Commerce Studio instance name already exists. Please choose a different name.';
          break;
        case 'E0001002':
          error.message = 'Invalid Commerce Studio configuration. Please check the provided settings.';
          break;
        case 'E0001003':
          error.message = 'Commerce Studio instance not found. It may have been deleted or moved.';
          break;
        case 'E0001004':
          error.message = 'Insufficient permissions to perform this operation on Commerce Studio instances.';
          break;
        case 'E0001005':
          error.message = 'Commerce Studio instance is currently in use and cannot be deleted.';
          break;
        default:
          if (!error.message || error.message.startsWith('E')) {
            error.message =
              'An error occurred while processing the Commerce Studio request. Please try again or contact support.';
          }
          break;
      }
    }
    throw error;
  }
}
